/**
 * FormF.jsx — Tools for Facilitating Reporting (Sheet F_Tools)
 *
 * Revision (Jun 2026) — full UI redesign matching Forms A/B/C/D/E aesthetic:
 *
 *   1. Slim single-line header — "Reporting Tools"
 *      (no "Form F", no "Sheet F_Tools" subtitle, no dark navy banner)
 *   2. AuditSidebar wrapper, same as A/B/C/D/E
 *   3. Two top-level tabs (matching Form C pattern):
 *        - Tab 01 · Cogeneration (CHP)
 *        - Tab 02 · Carbon Price
 *   4. Inside Cogeneration tab — sub-tabs for each CHP (matching Form E):
 *        - CHP1 / CHP2 / + Add
 *        - One CHP visible at a time, with sticky right panel showing live
 *          derived outputs (heat factor, elec factor, EFs, fuel split)
 *   5. Inside each CHP — FLAT compact layout (a–g sections):
 *        - No more 5 separate Tables for 1-row data each
 *        - Each section uses fc-sub-label header + compact grid of fields
 *        - All sections visible in one scroll
 *   6. Inside Carbon Price tab — wide table preserved (horizontal scroll)
 *        - Sticky right panel with live totals (CP due / Rebate / Effective CP)
 *        - Subtle "Precursors from Form E ↓" divider row between process rows
 *          and precursor rows
 *        - Currency selector inline with sticky panel header
 *   7. Color palette unified with A/B/C/D/E:
 *        - white = user-fill
 *        - lblue (#eff6ff) = auto-filled / auto-calculated
 *        - lgrey (#f1f5f9) = read-only / disabled
 *      Old palette (C6EFCE green, FFFF99 yellow, E7F2E3 lgreen, 1F4E79 navy,
 *      D4EDC4, FFF3CD, FFFEF5, DDEEFF) entirely gone.
 *   8. All status alerts/legend/progress-bar at the top REMOVED. Tab badges
 *      carry % completeness. Empty states render inline within each tab.
 *   9. Dark navy carbon price summary banner → sticky right panel with
 *      soft-card + accent-stripe pattern
 *  10. Currency dropdown moved into the Carbon Price tab header (compact)
 *  11. NEXT / Save & Continue → /summary/:auditId. Previous tab button on
 *      Carbon Price tab returns to Cogeneration.
 *
 * Behaviour preserved EXACTLY:
 *   - createEmptyCHP / recalcCHP (engine calls to F.mwhToTJ,
 *     F.chpAttributionFactors, F.chpHeatSpecificEF, F.chpFuelSplit)
 *   - CONSTANTS.CHP_HEAT_DEFAULT_EFF / CHP_ELEC_DEFAULT_EFF reference values
 *   - applyCalculations (F.applyCaronPriceCalculations) on every carbon row update
 *   - carbonRowFromProcess / carbonRowFromPrecursor / mergeAllIntoCarbonRows
 *     (Form D + Form E → carbon rows scaffold)
 *   - 96-currency CurrencyOptions list (intentionally richer than UNIT_GROUPS.CURRENCY)
 *   - updateCHPField / updateCarbonRow (field handlers)
 *   - calculateFormFProgress / sectionProgress (per-tab % completeness)
 *   - Firestore onSnapshot + reset-on-auditId pattern
 *   - saveToFirestore payload ({ chps, carbonRows, currency, progressMeta, updatedAt })
 *   - Form E (back) and /summary/ (forward) navigation
 **/

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, CircularProgress, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { firestore as db } from "./firebase.config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { useFormContext } from "../context/FormContext";
import { F as FFormulas, CONSTANTS } from "../utils/CbamFormulas";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";

import {
  calculateRequiredProgressForRows,
  FORM_F_REQUIRED_FIELDS,
} from "../utils/CbamProgress";

import { UNITS } from "../utils/Cbamunits";

// ─── Currency list (UNCHANGED — intentionally richer than UNIT_GROUPS.CURRENCY) ─
const CurrencyOptions = [
  "AED","AFN","ALL","AMD","ANG","AOA","ARS","AUD","AWG","AZN","BAM","BBD","BDT",
  "BGN","BHD","BIF","BMD","BND","BOB","BRL","BSD","BTN","BWP","BYN","BZD","CAD",
  "CDF","CHF","CLP","CNY","COP","CRC","CZK","DKK","DOP","DZD","EGP","EUR","GBP",
  "HKD","HNL","HRK","HUF","IDR","ILS","INR","IQD","IRR","ISK","JOD","JPY","KES",
  "KRW","KWD","KZT","LBP","LKR","MAD","MDL","MKD","MMK","MNT","MOP","MUR","MXN",
  "MYR","NGN","NOK","NPR","NZD","OMR","PEN","PHP","PKR","PLN","PYG","QAR","RON",
  "RSD","RUB","SAR","SCR","SEK","SGD","SYP","THB","TND","TRY","TWD","UAH","USD",
  "UYU","VEF","VND","ZAR",
];

// ─── CHP Factory + recalc (UNCHANGED engine calls) ────────────────────────────
const createEmptyCHP = () => ({
  title: "",
  fuelInputIntoCHP:             "",
  heatOutputFromCHP:            "",
  electricityOutputFromCHP_mwh: "",
  electricityOutputFromCHP_tj:  "",
  ghg_from_fuel_input:          "",
  ghg_from_flue_gas_cleaning:   "",
  ghg_total:                    "",
  default_eff_heat:             "55.00",
  default_eff_elec:             "25.00",
  eff_heat_production:          "",
  eff_electricity_production:   "",
  _heatFactor:                  "",
  _elecFactor:                  "",
  ref_eff_heat_production:        String(CONSTANTS.CHP_HEAT_DEFAULT_EFF * 100),
  ref_eff_electricity_production: String(CONSTANTS.CHP_ELEC_DEFAULT_EFF * 100),
  emissions_attr_heat:          "",
  emission_factor_heat:         "",
  emissions_attr_electricity:   "",
  emission_factor_electricity:  "",
  fuel_input_for_heat:          "",
  fuel_input_for_electricity:   "",
});

const recalcCHP = (chp) => {
  const p   = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
  const fmt = (v) => (v === 0 || !Number.isFinite(v)) ? "" : String(parseFloat(v.toFixed(6)));

  const electricityTJ = FFormulas.mwhToTJ(chp.electricityOutputFromCHP_mwh);
  const ghgTotal      = p(chp.ghg_from_fuel_input) + p(chp.ghg_from_flue_gas_cleaning);

  const { heatFactor, elecFactor } = FFormulas.chpAttributionFactors(
    chp.heatOutputFromCHP,
    chp.fuelInputIntoCHP,
    electricityTJ || chp.electricityOutputFromCHP_tj
  );

  const heatEffFrac = p(chp.ref_eff_heat_production)       / 100 || CONSTANTS.CHP_HEAT_DEFAULT_EFF;
  const elecEffFrac = p(chp.ref_eff_electricity_production) / 100 || CONSTANTS.CHP_ELEC_DEFAULT_EFF;

  const efHeat = FFormulas.chpHeatSpecificEF(heatFactor, ghgTotal, heatEffFrac);
  const efElec = FFormulas.chpHeatSpecificEF(elecFactor, ghgTotal, elecEffFrac);

  const emissionsHeat = p(heatFactor) * p(ghgTotal);
  const emissionsElec = p(elecFactor) * p(ghgTotal);

  const { heatFuel, elecFuel } = FFormulas.chpFuelSplit(
    chp.heatOutputFromCHP,
    electricityTJ || chp.electricityOutputFromCHP_tj,
    chp.fuelInputIntoCHP,
    heatEffFrac,
    elecEffFrac
  );

  return {
    ...chp,
    electricityOutputFromCHP_tj: fmt(electricityTJ),
    ghg_total:                   fmt(ghgTotal),
    _heatFactor:                 fmt(heatFactor),
    _elecFactor:                 fmt(elecFactor),
    emissions_attr_heat:         fmt(emissionsHeat),
    emission_factor_heat:        fmt(efHeat),
    emissions_attr_electricity:  fmt(emissionsElec),
    emission_factor_electricity: fmt(efElec),
    fuel_input_for_heat:         fmt(heatFuel),
    fuel_input_for_electricity:  fmt(elecFuel),
  };
};

// ─── Carbon row helpers (UNCHANGED) ───────────────────────────────────────────
const applyCalculations = (row) => FFormulas.applyCaronPriceCalculations(row);

const carbonRowFromProcess = (proc, index, existing = {}) => {
  const row = {
    rowType:                 "process",
    rowLabel:                `P${index + 1}`,
    processId:               proc.processId || `process-${index + 1}`,
    productionProcess:       proc.title?.trim() || proc.processId || `Production process ${index + 1}`,
    aggregatedGoodsCategory: proc.aggregatedGoodsCategory || "",
    se_direct:        proc.see_direct   != null ? String(proc.see_direct)   : "",
    se_indirect:      proc.see_indirect != null ? String(proc.see_indirect) : "",
    se_total_tCO2e_t: proc.see_total    != null ? String(proc.see_total)    : "",
    share_of_emissions_covered: existing.share_of_emissions_covered || "",
    carbon_price_due_local:     existing.carbon_price_due_local     || "",
    amount_of_rebate_local:     existing.amount_of_rebate_local     || "",
    covered_se_tCO2e_t:           "",
    cp_due_per_produced_t_or_MWh: "",
    rebate_per_produced_t_or_MWh: "",
    result_effective_cp:          "",
  };
  return applyCalculations(row);
};

const carbonRowFromPrecursor = (prec, index, existing = {}) => {
  const seeDirect   = parseFloat(prec.emissions?.seeDirect   || 0);
  const seeIndirect = parseFloat(prec.emissions?.seeIndirect || 0);
  const seeTotal    = seeDirect + seeIndirect;

  const row = {
    rowType:                 "precursor",
    rowLabel:                `PP${index + 1}`,
    processId:               prec.precursorId || prec.id || `precursor-${index + 1}`,
    productionProcess:       prec.precursorName || prec.name || `Purchased precursor ${index + 1}`,
    aggregatedGoodsCategory: prec.goodsCategory || prec.aggregatedGoodsCategory || "",
    se_direct:        String(seeDirect),
    se_indirect:      String(seeIndirect),
    se_total_tCO2e_t: String(seeTotal),
    share_of_emissions_covered: existing.share_of_emissions_covered || "0",
    carbon_price_due_local:     existing.carbon_price_due_local     || "",
    amount_of_rebate_local:     existing.amount_of_rebate_local     || "",
    covered_se_tCO2e_t:           "",
    cp_due_per_produced_t_or_MWh: "",
    rebate_per_produced_t_or_MWh: "",
    result_effective_cp:          "",
  };
  return applyCalculations(row);
};

const mergeAllIntoCarbonRows = (formDProcesses = [], formEPrecursors = [], savedRows = []) => {
  const processRows = formDProcesses.map((proc, i) => {
    const existing = savedRows.find(r => r.processId === proc.processId && r.rowType === "process") || {};
    return carbonRowFromProcess(proc, i, existing);
  });

  const precursorRows = formEPrecursors.map((prec, i) => {
    const precId   = prec.precursorId || prec.id || `precursor-${i + 1}`;
    const existing = savedRows.find(r => r.processId === precId && r.rowType === "precursor") || {};
    return carbonRowFromPrecursor(prec, i, existing);
  });

  return [...processRows, ...precursorRows];
};

const defaultChps = () => [createEmptyCHP(), createEmptyCHP()].map(recalcCHP);

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .ff { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; color: #161616; }

  /* Slim header */
  .ff-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .ff-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .ff-body {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 14px clamp(20px, 3vw, 32px) 20px;
  }

  /* Sector chip strip */
  .ff-sector-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #475569;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .ff-sector-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 11px;
  }

  /* Top-level tabs */
  .ff-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 14px;
    overflow-x: auto;
  }
  .ff-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .ff-tab:hover:not(.active) { background: #f1f5f9; color: #1e293b; }
  .ff-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  .ff-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .ff-tab.active .ff-tab-num { background: #2563eb; color: #fff; }
  .ff-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .ff-tab-badge.done  { background: #DCFCE7; color: #15803D; }
  .ff-tab-badge.warn  { background: #FEF3C7; color: #D97706; }
  .ff-tab-badge.empty { background: #FEE2E2; color: #DC2626; }

  /* Two-column layout: content + sticky right panel */
  .ff-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 16px;
  }
  @media (max-width: 1100px) {
    .ff-grid { grid-template-columns: 1fr; }
  }
  .ff-content { min-width: 0; }
  .ff-sticky-col { position: relative; }
  .ff-sticky {
    position: sticky;
    top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Sub-tabs (CHP1/CHP2/+) */
  .ff-subtabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #cbd5e1;
    margin-bottom: 12px;
  }
  .ff-subtab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 12px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .ff-subtab:hover:not(.active) { background: #f8fafc; color: #1e293b; }
  .ff-subtab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    border-bottom: 1px solid #fff;
    background: #fff;
    margin-bottom: -1px;
  }
  .ff-subtab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 1px 6px;
    border-radius: 3px;
    letter-spacing: 0.4px;
  }
  .ff-subtab.active .ff-subtab-num { background: #2563eb; color: #fff; }
  .ff-subtab-add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 7px 12px;
    font-size: 11px;
    color: #2563eb;
    font-weight: 600;
    cursor: pointer;
    border: 1px dashed #93c5fd;
    border-bottom: none;
    background: transparent;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    white-space: nowrap;
    transition: background 0.15s;
  }
  .ff-subtab-add:hover { background: #eff6ff; }

  /* CHP panel */
  .ff-chp-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 6px 6px;
    padding: 14px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  }

  /* CHP title row */
  .ff-chp-title-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: end;
    padding-bottom: 10px;
    margin-bottom: 12px;
    border-bottom: 1px dashed #e2e8f0;
  }
  .ff-chp-title-label {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.5px;
    margin-bottom: 3px;
  }
  .ff-chp-title-input {
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 7px 10px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #161616;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }
  .ff-chp-title-input:focus { border-color: #2563eb; }

  /* Sub-label */
  .ff-sub-label {
    font-size: 11px;
    font-weight: 700;
    color: #1e293b;
    margin: 14px 0 8px;
    padding-bottom: 3px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }
  .ff-sub-label:first-child { margin-top: 0; }
  .ff-sub-label .hint {
    font-size: 9px;
    color: #64748b;
    font-weight: 500;
    margin-left: 6px;
    letter-spacing: 0;
    text-transform: none;
  }

  /* Compact field grid */
  .ff-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .ff-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
  .ff-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; }

  .ff-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .ff-field-label {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .ff-field-unit {
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    margin-left: 4px;
  }
  .ff-field-hint {
    font-size: 9px;
    color: #2563eb;
    font-style: italic;
    margin-left: 4px;
  }

  /* Inputs */
  .ff-input, .ff-select {
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 6px 8px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s, background 0.15s;
  }
  .ff-input:focus, .ff-select:focus { border-color: #2563eb; }
  .ff-input.lblue, .ff-select.lblue {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
  .ff-input.lblue:read-only { cursor: default; }
  .ff-input.lgrey, .ff-select.lgrey {
    background: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }

  /* Inline note for default-efficiency context */
  .ff-note {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: #1e40af;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 6px 10px;
    border-radius: 4px;
    margin: 4px 0 10px;
  }

  /* Carbon Price table */
  .ff-cp-table-wrap {
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    overflow-x: auto;
    background: #fff;
  }
  .ff-cp-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1200px;
    font-size: 12px;
  }
  .ff-cp-table thead th {
    background: #f8fafc;
    border-bottom: 1px solid #cbd5e1;
    border-right: 1px solid #f1f5f9;
    padding: 8px 10px;
    font-size: 10px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-align: left;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .ff-cp-table thead th:last-child { border-right: none; }
  .ff-cp-table thead th .unit {
    display: block;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    margin-top: 2px;
  }
  .ff-cp-table tbody td {
    padding: 4px 8px;
    border-bottom: 1px solid #f1f5f9;
    border-right: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .ff-cp-table tbody td:last-child { border-right: none; }
  .ff-cp-table tbody tr:last-child td { border-bottom: none; }
  .ff-cp-table tbody tr.precursor td { background: #fefce8; }
  .ff-cp-table tbody tr.divider td {
    background: #f1f5f9;
    color: #475569;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 6px 10px;
    border-top: 1px solid #cbd5e1;
  }
  .ff-cp-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: #1e40af;
    text-align: center;
    background: #eff6ff;
    padding: 3px 7px;
    border-radius: 3px;
    display: inline-block;
  }
  .ff-cp-table tbody tr.precursor .ff-cp-label {
    color: #92400e;
    background: #fef3c7;
  }
  .ff-cp-cell-input {
    width: 100%;
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 5px 7px;
    border-radius: 3px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 12px;
    color: #161616;
    outline: none;
    box-sizing: border-box;
  }
  .ff-cp-cell-input:focus { border-color: #2563eb; }
  .ff-cp-cell-input.lblue { background: #eff6ff; border-color: #bfdbfe; }
  .ff-cp-cell-input.lblue:read-only { cursor: default; }
  .ff-cp-cell-input.lgrey { background: #f1f5f9; border-color: #e2e8f0; color: #94a3b8; cursor: not-allowed; }

  /* Sticky right panel — soft card + accent stripe (matches B/C/D/E) */
  .ff-summary-card {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 14px 14px 14px 20px;
    overflow: hidden;
  }
  .ff-summary-card::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .ff-summary-card-title {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .ff-summary-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 5px 0;
    border-bottom: 1px solid rgba(203,213,225,0.5);
  }
  .ff-summary-item:last-of-type { border-bottom: none; }
  .ff-summary-item .k {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
  }
  .ff-summary-item .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 13px;
    color: #1e293b;
  }
  .ff-summary-item .v.zero { opacity: 0.4; }
  .ff-summary-item .unit {
    font-size: 9px;
    color: #94a3b8;
    margin-left: 4px;
  }
  .ff-summary-grand {
    border-top: 1px solid #cbd5e1;
    margin-top: 10px;
    padding-top: 10px;
  }
  .ff-summary-grand .k {
    font-size: 10px;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .ff-summary-grand .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 22px;
    color: #1e40af;
    letter-spacing: -0.4px;
  }
  .ff-summary-grand .v.zero { color: #94a3b8; }
  .ff-summary-grand .unit { font-size: 10px; color: #64748b; margin-left: 4px; }

  /* Currency selector inside summary card */
  .ff-currency-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(203,213,225,0.5);
  }
  .ff-currency-row .k {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .ff-currency-row select {
    flex: 1;
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 4px 7px;
    border-radius: 3px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 12px;
    color: #161616;
    outline: none;
  }
  .ff-currency-row select:focus { border-color: #2563eb; }

  /* Footer */
  .ff-footer-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }
  .ff-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
  }
  .ff-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .ff-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }
  .ff-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: inherit; font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .ff-btn-save:hover { background: #1d4ed8; }
  .ff-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }
  .ff-btn-delete {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 6px 10px;
    background: transparent; color: #dc2626;
    border: 1px solid #fca5a5;
    font-family: inherit; font-size: 11px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .ff-btn-delete:hover:not(:disabled) { background: #fef2f2; }
  .ff-btn-delete:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Toast */
  .ff-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .ff-toast.error { background: #dc2626; }

  /* Empty state inline */
  .ff-empty {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    padding: 30px 20px;
    text-align: center;
    color: #64748b;
    font-size: 12px;
  }
  .ff-empty strong { color: #1e293b; }
`;

// ─── Number formatting helper ─────────────────────────────────────────────────
const fmt = (v) => {
  if (v === "" || v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
};

// ═════════════════════════════════════════════════════════════════════════════
const FormF = () => {
  const navigate    = useNavigate();
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData, updateForm } = useFormContext();

  const installSector = allFormData?.formA?.sector || "";

  // Source data from context
  const formDProcesses  = allFormData?.formD?.processes    || [];
  const formEPrecursors = allFormData?.formE?.precursors   || [];

  // ── State ──────────────────────────────────────────────────────────────────
  const [chps,        setChps]        = useState(defaultChps());
  const [carbonRows,  setCarbonRows]  = useState([]);
  const [currency,    setCurrency]    = useState("EUR");
  const [activeTab,   setActiveTab]   = useState("cogeneration");
  const [activeChpIdx, setActiveChpIdx] = useState(0);
  const [isLoading,   setIsLoading]   = useState(true);
  const [isSaving,    setIsSaving]    = useState(false);
  const [toast,       setToast]       = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Re-merge when FormD or FormE updates (UNCHANGED behaviour) ────────────
  useEffect(() => {
    if (isLoading) return;
    setCarbonRows(prev => mergeAllIntoCarbonRows(formDProcesses, formEPrecursors, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFormData?.formD?.processes, allFormData?.formE?.precursors, isLoading]);

  // ── Sync to context (UNCHANGED — only after Firestore settles) ───────────
  useEffect(() => {
    if (isLoading) return;
    updateForm("formF", { chps, carbonRows, currency });
  }, [chps, carbonRows, currency, isLoading, updateForm]);

  // ── Firestore sync (UNCHANGED) ────────────────────────────────────────────
  useEffect(() => {
    if (!auditId) return;
    setChps(defaultChps());
    setCarbonRows([]);
    setCurrency("EUR");
    setIsLoading(true);

    const docRef = doc(db, "auditReports", auditId, "form_f", "main");
    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.chps?.length)  setChps(data.chps.map(recalcCHP));
        if (data.currency)      setCurrency(data.currency);
        if (data.carbonRows) {
          setCarbonRows(mergeAllIntoCarbonRows(formDProcesses, formEPrecursors, data.carbonRows));
        } else {
          setCarbonRows(mergeAllIntoCarbonRows(formDProcesses, formEPrecursors, []));
        }
      } else {
        setCarbonRows(mergeAllIntoCarbonRows(formDProcesses, formEPrecursors, []));
      }
      setIsLoading(false);
    }, err => { console.error(err); setIsLoading(false); });

    return () => unsub();
    // eslint-disable-next-line
  }, [auditId]);

  // ── Helpers (UNCHANGED) ────────────────────────────────────────────────────
  const isFilled = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return !Number.isNaN(value);
    if (typeof value === "boolean") return true;
    return false;
  };

  const activeChps = chps.filter((chp) =>
    chp.fuelInputIntoCHP ||
    chp.heatOutputFromCHP ||
    chp.electricityOutputFromCHP_mwh ||
    chp.ghg_from_fuel_input ||
    chp.ghg_from_flue_gas_cleaning
  );

  const sectionProgress = {
    cogeneration: calculateRequiredProgressForRows(
      activeChps.length > 0 ? activeChps : [],
      FORM_F_REQUIRED_FIELDS.cogeneration
    ),
    carbonPrice: calculateRequiredProgressForRows(
      carbonRows,
      FORM_F_REQUIRED_FIELDS.carbonPrice
    ),
  };

  const calculateFormFProgress = () => {
    const editableValues = [];
    chps.forEach((chp) => {
      editableValues.push(chp.fuelInputIntoCHP);
      editableValues.push(chp.heatOutputFromCHP);
      editableValues.push(chp.electricityOutputFromCHP_mwh);
      editableValues.push(chp.ghg_from_fuel_input);
      editableValues.push(chp.ghg_from_flue_gas_cleaning);
    });
    carbonRows.forEach((row) => {
      editableValues.push(row.share_of_emissions_covered);
      editableValues.push(row.carbon_price_due_local);
      editableValues.push(row.amount_of_rebate_local);
    });
    const total  = editableValues.length;
    const filled = editableValues.filter((value) => value === "-" || isFilled(value)).length;
    const progress = total === 0 ? 0 : Math.round((filled / total) * 100);
    return {
      editableTotal: total,
      editableFilled: filled,
      progress,
      status: progress >= 100 ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
    };
  };

  // ── Save (UNCHANGED) ──────────────────────────────────────────────────────
  const saveToFirestore = async () => {
    if (!auditId) return;
    const docRef = doc(db, "auditReports", auditId, "form_f", "main");
    const progressMeta = calculateFormFProgress();
    const payload = { chps, carbonRows, currency, progressMeta, updatedAt: new Date() };
    await setDoc(docRef, payload, { merge: true });
    updateForm("formF", payload);
  };

  const handleSaveAndNext = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      showToast("Form F saved");
      setTimeout(() => navigate(`/summary/${auditId}`), 500);
    } catch (err) {
      console.error(err);
      showToast("Save failed", "error");
    } finally { setIsSaving(false); }
  };

  const handleBack = async () => {
    setIsSaving(true);
    try { await saveToFirestore(); navigate(`/form-e/${auditId}`); }
    catch (err) { navigate(`/form-e/${auditId}`); }
    finally { setIsSaving(false); }
  };

  const handleNextTab = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      setActiveTab("carbon");
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast("Saved");
    } catch (e) { console.error(e); showToast("Save failed", "error"); }
    finally { setIsSaving(false); }
  };

  const handlePrevTab = () => {
    setActiveTab("cogeneration");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Updaters (UNCHANGED) ──────────────────────────────────────────────────
  const updateCHPField = (index, key, value) => {
    setChps(prev => {
      const copy = structuredClone(prev);
      copy[index][key] = value;
      copy[index] = recalcCHP(copy[index]);
      return copy;
    });
  };

  const updateCarbonRow = (rowIndex, key, value) => {
    setCarbonRows(prev => {
      const copy = structuredClone(prev);
      copy[rowIndex][key] = value;
      copy[rowIndex] = applyCalculations(copy[rowIndex]);
      return copy;
    });
  };

  const handleAddCHP = () => {
    setChps(prev => {
      const next = [...prev, recalcCHP(createEmptyCHP())];
      setActiveChpIdx(next.length - 1);
      return next;
    });
  };

  const handleRemoveCHP = (index) => {
    if (chps.length <= 1) return;
    setChps(prev => prev.filter((_, i) => i !== index));
    setActiveChpIdx(0);
  };

  // ── Summary totals ─────────────────────────────────────────────────────────
  const totalCPDue     = carbonRows.reduce((s, r) => s + (parseFloat(r.cp_due_per_produced_t_or_MWh) || 0), 0);
  const totalRebate    = carbonRows.reduce((s, r) => s + (parseFloat(r.rebate_per_produced_t_or_MWh) || 0), 0);
  const totalEffective = carbonRows.reduce((s, r) => s + (parseFloat(r.result_effective_cp)          || 0), 0);

  const processCount   = carbonRows.filter(r => r.rowType === "process").length;
  const precursorCount = carbonRows.filter(r => r.rowType === "precursor").length;

  const tabBadgeClass = (pct) =>
    pct === 100 ? "done" : pct > 0 ? "warn" : "empty";

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // COGENERATION TAB — sub-tabs for CHPs + flat compact layout per CHP
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCHP = (chp, idx) => {
    //  Total efficiency derived from heat + elec factors
    const heatF = parseFloat(chp._heatFactor) || 0;
    const elecF = parseFloat(chp._elecFactor) || 0;
    const totalFactor = heatF + elecF;
    const hasOutput = totalFactor !== 0 || chp.ghg_total;

    return (
      <div className="ff-grid">

        {/* LEFT: CHP content */}
        <div className="ff-content">
          <div className="ff-chp-panel">

            {/* Title + delete */}
            <div className="ff-chp-title-row">
              <div>
                <div className="ff-chp-title-label">CHP unit name (optional)</div>
                <input
                  className="ff-chp-title-input"
                  value={chp.title || ""}
                  onChange={(e) => updateCHPField(idx, "title", e.target.value)}
                  placeholder={`CHP Tool ${idx + 1}`}
                />
              </div>
              <button
                className="ff-btn-delete"
                onClick={() => handleRemoveCHP(idx)}
                disabled={chps.length === 1}
                title={chps.length === 1 ? "Cannot delete the only CHP" : "Delete this CHP"}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} /> Delete
              </button>
            </div>

            {/* (a) Inputs and outputs */}
            <div className="ff-sub-label">(a) Inputs and outputs</div>
            <div className="ff-grid-4">
              <div className="ff-field">
                <div className="ff-field-label">Fuel input <span className="ff-field-unit">{UNITS.TJ}</span></div>
                <input className="ff-input" type="number"
                  value={chp.fuelInputIntoCHP}
                  onChange={(e) => updateCHPField(idx, "fuelInputIntoCHP", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Heat output <span className="ff-field-unit">{UNITS.TJ}</span></div>
                <input className="ff-input" type="number"
                  value={chp.heatOutputFromCHP}
                  onChange={(e) => updateCHPField(idx, "heatOutputFromCHP", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Electricity output <span className="ff-field-unit">{UNITS.MWH}</span></div>
                <input className="ff-input" type="number"
                  value={chp.electricityOutputFromCHP_mwh}
                  onChange={(e) => updateCHPField(idx, "electricityOutputFromCHP_mwh", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">
                  Electricity output <span className="ff-field-unit">{UNITS.TJ}</span>
                  <span className="ff-field-hint">auto: ×0.0036</span>
                </div>
                <input className="ff-input lblue" readOnly value={chp.electricityOutputFromCHP_tj || ""} />
              </div>
            </div>

            {/* (b) Total emissions */}
            <div className="ff-sub-label">(b) Total emissions from CHP <span className="hint">{UNITS.TCO2}</span></div>
            <div className="ff-grid-3">
              <div className="ff-field">
                <div className="ff-field-label">From fuel input</div>
                <input className="ff-input" type="number"
                  value={chp.ghg_from_fuel_input}
                  onChange={(e) => updateCHPField(idx, "ghg_from_fuel_input", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">From flue gas cleaning</div>
                <input className="ff-input" type="number"
                  value={chp.ghg_from_flue_gas_cleaning}
                  onChange={(e) => updateCHPField(idx, "ghg_from_flue_gas_cleaning", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">
                  Total emissions <span className="ff-field-hint">auto: sum</span>
                </div>
                <input className="ff-input lblue" readOnly value={chp.ghg_total || ""} />
              </div>
            </div>

            {/* (c) Default efficiencies */}
            <div className="ff-sub-label">(c) Default efficiencies <span className="hint">EU MRR Article 22</span></div>
            <div className="ff-note">
              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
              Typical EU MRR values: heat 55%, electricity 25%. Adjust if your installation uses different defaults.
            </div>
            <div className="ff-grid-2">
              <div className="ff-field">
                <div className="ff-field-label">Heat <span className="ff-field-unit">%</span></div>
                <input className="ff-input" type="number"
                  value={chp.default_eff_heat}
                  onChange={(e) => updateCHPField(idx, "default_eff_heat", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Electricity <span className="ff-field-unit">%</span></div>
                <input className="ff-input" type="number"
                  value={chp.default_eff_elec}
                  onChange={(e) => updateCHPField(idx, "default_eff_elec", e.target.value)} />
              </div>
            </div>

            {/* (d) Attribution factors */}
            <div className="ff-sub-label">
              (d) Attribution factors <span className="hint">auto-calculated</span>
            </div>
            <div className="ff-grid-3">
              <div className="ff-field">
                <div className="ff-field-label">Heat production</div>
                <input className="ff-input lblue" readOnly value={chp._heatFactor || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Electricity production</div>
                <input className="ff-input lblue" readOnly value={chp._elecFactor || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Total</div>
                <input className="ff-input lblue" readOnly
                  value={
                    chp._heatFactor && chp._elecFactor
                      ? String(parseFloat((heatF + elecF).toFixed(6)))
                      : ""
                  } />
              </div>
            </div>

            {/* (e) Reference efficiencies */}
            <div className="ff-sub-label">(e) Reference efficiencies <span className="hint">%</span></div>
            <div className="ff-grid-2">
              <div className="ff-field">
                <div className="ff-field-label">Heat production</div>
                <input className="ff-input" type="number" placeholder="88"
                  value={chp.ref_eff_heat_production}
                  onChange={(e) => updateCHPField(idx, "ref_eff_heat_production", e.target.value)} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Electricity production</div>
                <input className="ff-input" type="number" placeholder="44.2"
                  value={chp.ref_eff_electricity_production}
                  onChange={(e) => updateCHPField(idx, "ref_eff_electricity_production", e.target.value)} />
              </div>
            </div>

            {/* (f) Attributable emissions */}
            <div className="ff-sub-label">
              (f) Emissions attributable to heat and electricity <span className="hint">auto-calculated</span>
            </div>
            <div className="ff-grid-4">
              <div className="ff-field">
                <div className="ff-field-label">Emissions → heat <span className="ff-field-unit">{UNITS.TCO2}</span></div>
                <input className="ff-input lblue" readOnly value={chp.emissions_attr_heat || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">EF heat <span className="ff-field-unit">{UNITS.TCO2_PER_TJ}</span></div>
                <input className="ff-input lblue" readOnly value={chp.emission_factor_heat || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Emissions → electricity <span className="ff-field-unit">{UNITS.TCO2}</span></div>
                <input className="ff-input lblue" readOnly value={chp.emissions_attr_electricity || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">EF electricity <span className="ff-field-unit">{UNITS.TCO2_PER_TJ}</span></div>
                <input className="ff-input lblue" readOnly value={chp.emission_factor_electricity || ""} />
              </div>
            </div>

            {/* (g) Fuel split */}
            <div className="ff-sub-label">
              (g) Fuel input split <span className="hint">auto-calculated · {UNITS.TJ}</span>
            </div>
            <div className="ff-grid-2">
              <div className="ff-field">
                <div className="ff-field-label">Fuel input for heat</div>
                <input className="ff-input lblue" readOnly value={chp.fuel_input_for_heat || ""} />
              </div>
              <div className="ff-field">
                <div className="ff-field-label">Fuel input for electricity</div>
                <input className="ff-input lblue" readOnly value={chp.fuel_input_for_electricity || ""} />
              </div>
            </div>

          </div>
        </div>

        {/* RIGHT: sticky panel — CHP live outputs */}
        <div className="ff-sticky-col">
          <div className="ff-sticky">
            <div className="ff-summary-card">
              <div className="ff-summary-card-title">
                CHP {idx + 1} · live outputs
              </div>

              <div className="ff-summary-item">
                <span className="k">Total emissions</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp.ghg_total)}</span>
                  <span className="unit">{UNITS.TCO2}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Heat factor</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp._heatFactor)}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Elec factor</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp._elecFactor)}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">EF heat</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp.emission_factor_heat)}</span>
                  <span className="unit">{UNITS.TCO2_PER_TJ}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">EF electricity</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp.emission_factor_electricity)}</span>
                  <span className="unit">{UNITS.TCO2_PER_TJ}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Fuel → heat</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp.fuel_input_for_heat)}</span>
                  <span className="unit">{UNITS.TJ}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Fuel → electricity</span>
                <span>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(chp.fuel_input_for_electricity)}</span>
                  <span className="unit">{UNITS.TJ}</span>
                </span>
              </div>

              <div className="ff-summary-grand">
                <div className="k">Total attribution</div>
                <div>
                  <span className={`v ${!hasOutput ? "zero" : ""}`}>{fmt(totalFactor)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCogenerationTab = () => {
    const activeCHP = chps[activeChpIdx];

    return (
      <>
        {/* Sub-tabs for CHPs */}
        <div className="ff-subtabs">
          {chps.map((chp, i) => (
            <div
              key={i}
              className={`ff-subtab ${i === activeChpIdx ? "active" : ""}`}
              onClick={() => setActiveChpIdx(i)}
            >
              <span className="ff-subtab-num">{String(i+1).padStart(2,"0")}</span>
              <span>{chp.title?.trim() || `CHP ${i + 1}`}</span>
            </div>
          ))}
          <button className="ff-subtab-add" onClick={handleAddCHP}>
            <AddIcon sx={{ fontSize: 13 }} /> Add CHP
          </button>
        </div>

        {/* Active CHP */}
        {activeCHP && renderCHP(activeCHP, activeChpIdx)}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CARBON PRICE TAB — wide table + sticky summary panel
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCarbonPriceTab = () => {

    if (carbonRows.length === 0) {
      return (
        <div className="ff-empty">
          <div><strong>No rows to display.</strong></div>
          <div style={{ marginTop: 6 }}>Complete Form D (for processes) and Form E (for precursors) first.</div>
        </div>
      );
    }

    //  Find the index where precursors start (for divider row insertion)
    const firstPrecIdx = carbonRows.findIndex(r => r.rowType === "precursor");

    return (
      <div className="ff-grid">

        {/* LEFT: wide carbon price table */}
        <div className="ff-content">
          <div className="ff-cp-table-wrap">
            <table className="ff-cp-table">
              <thead>
                <tr>
                  <th>—</th>
                  <th>Production process<span className="unit">from Form D / Form E</span></th>
                  <th>Aggregated goods<span className="unit">category</span></th>
                  <th>SE total<span className="unit">{UNITS.TCO2E_PER_T}</span></th>
                  <th>Share covered<span className="unit">0–1</span></th>
                  <th>Covered SE<span className="unit">{UNITS.TCO2E_PER_T}</span></th>
                  <th>Carbon price due<span className="unit">{currency}/{UNITS.TCO2E}</span></th>
                  <th>Rebate<span className="unit">{currency}/{UNITS.TCO2E}</span></th>
                  <th>CP due<span className="unit">per t/MWh</span></th>
                  <th>Rebate<span className="unit">per t/MWh</span></th>
                  <th>Effective CP<span className="unit">per t/MWh</span></th>
                </tr>
              </thead>
              <tbody>
                {carbonRows.map((row, i) => {
                  const isPrec = row.rowType === "precursor";
                  //  Insert divider row at the boundary between process & precursor rows
                  const showDivider = i === firstPrecIdx && firstPrecIdx > 0;

                  return (
                    <React.Fragment key={row.processId || i}>
                      {showDivider && (
                        <tr className="divider">
                          <td colSpan={11}>↓ Precursors from Form E</td>
                        </tr>
                      )}
                      <tr className={isPrec ? "precursor" : ""}>
                        <td><span className="ff-cp-label">{row.rowLabel}</span></td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.productionProcess} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.aggregatedGoodsCategory} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.se_total_tCO2e_t || "0"} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input" type="number" placeholder="0–1"
                            value={row.share_of_emissions_covered}
                            onChange={(e) => updateCarbonRow(i, "share_of_emissions_covered", e.target.value)} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.covered_se_tCO2e_t} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input" type="number"
                            value={row.carbon_price_due_local}
                            onChange={(e) => updateCarbonRow(i, "carbon_price_due_local", e.target.value)} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input" type="number"
                            value={row.amount_of_rebate_local}
                            onChange={(e) => updateCarbonRow(i, "amount_of_rebate_local", e.target.value)} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.cp_due_per_produced_t_or_MWh} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.rebate_per_produced_t_or_MWh} />
                        </td>
                        <td>
                          <input className="ff-cp-cell-input lblue" readOnly
                            value={row.result_effective_cp} />
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "text-bottom" }} />
            Covered SE = SEE_total × Share. CP due = Covered SE × Carbon price.
            Effective CP = CP_due − (Covered_SE × Rebate). All calculated via F.applyCaronPriceCalculations().
            Process SEE from D.calculateSEE() (Annex IV). Precursor SEE = seeDirect + seeIndirect from Form E.
          </div>
        </div>

        {/* RIGHT: sticky summary with currency + totals */}
        <div className="ff-sticky-col">
          <div className="ff-sticky">
            <div className="ff-summary-card">
              <div className="ff-summary-card-title">
                Carbon price summary
              </div>

              {/* Currency selector inline */}
              <div className="ff-currency-row">
                <span className="k">Currency</span>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CurrencyOptions.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="ff-summary-item">
                <span className="k">Processes</span>
                <span>
                  <span className="v">{processCount}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Precursors</span>
                <span>
                  <span className="v">{precursorCount}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Total CP due</span>
                <span>
                  <span className={`v ${totalCPDue === 0 ? "zero" : ""}`}>{fmt(totalCPDue)}</span>
                  <span className="unit">{currency}</span>
                </span>
              </div>
              <div className="ff-summary-item">
                <span className="k">Total rebate</span>
                <span>
                  <span className={`v ${totalRebate === 0 ? "zero" : ""}`}>{fmt(totalRebate)}</span>
                  <span className="unit">{currency}</span>
                </span>
              </div>

              <div className="ff-summary-grand">
                <div className="k">Total effective CP</div>
                <div>
                  <span className={`v ${totalEffective === 0 ? "zero" : ""}`}>{fmt(totalEffective)}</span>
                  <span className="unit">{currency}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <style>{STYLES}</style>

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AuditSidebar displayAuditId={auditId} formsData={formsData} />

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div className="ff">

            {/* Slim header */}
            <div className="ff-header-slim">
              <div className="title">Reporting Tools</div>
            </div>

            <div className="ff-body">

              {/* Sector chip (FYI) */}
              {installSector && (
                <div className="ff-sector-bar">
                  <span>Sector:</span>
                  <span className="ff-sector-chip">{installSector}</span>
                  <Tooltip
                    title="Carbon price tool credits any carbon pricing already paid in the home jurisdiction against CBAM liability. Cogeneration tool splits CHP fuel/emissions between heat and electricity outputs."
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8", cursor: "help" }} />
                  </Tooltip>
                </div>
              )}

              {/* Top-level tabs */}
              <div className="ff-tabs">
                <div
                  className={`ff-tab ${activeTab === "cogeneration" ? "active" : ""}`}
                  onClick={() => setActiveTab("cogeneration")}
                >
                  <span className="ff-tab-num">01</span>
                  <span>Cogeneration (CHP)</span>
                  <span className={`ff-tab-badge ${tabBadgeClass(sectionProgress.cogeneration.progress)}`}>
                    {sectionProgress.cogeneration.progress}%
                  </span>
                </div>
                <div
                  className={`ff-tab ${activeTab === "carbon" ? "active" : ""}`}
                  onClick={() => setActiveTab("carbon")}
                >
                  <span className="ff-tab-num">02</span>
                  <span>Carbon Price</span>
                  <span className={`ff-tab-badge ${tabBadgeClass(sectionProgress.carbonPrice.progress)}`}>
                    {sectionProgress.carbonPrice.progress}%
                  </span>
                </div>
              </div>

              {/* Active tab */}
              {activeTab === "cogeneration" && renderCogenerationTab()}
              {activeTab === "carbon"       && renderCarbonPriceTab()}

              {/* Footer */}
              <div className="ff-footer-bar">
                <div>
                  {activeTab === "carbon" ? (
                    <button className="ff-btn-prev" onClick={handlePrevTab} disabled={isSaving}>
                      ← Previous tab
                    </button>
                  ) : (
                    <button className="ff-btn-prev" onClick={handleBack} disabled={isSaving}>
                      ← Back
                    </button>
                  )}
                </div>
                {activeTab === "cogeneration" ? (
                  <button className="ff-btn-save" onClick={handleNextTab} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Next →"}
                  </button>
                ) : (
                  <button className="ff-btn-save" onClick={handleSaveAndNext} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save & Go to Summary →"}
                  </button>
                )}
              </div>
            </div>

            {toast && (
              <div className={`ff-toast ${toast.type === "error" ? "error" : ""}`}>
                {toast.type === "error" ? "×" : "✓"} {toast.msg}
              </div>
            )}
          </div>
        </Box>
      </Box>
    </>
  );
};

export default FormF;
